import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { ShoppingCart, Plus, Minus, Store, X, ChevronDown, ShieldAlert, WifiOff, Star, Search, ArrowDownAZ, CheckCircle2 } from 'lucide-react';
import logger from '../../lib/logger';
import VegIndicator from '../../components/VegIndicator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical allergen taxonomy — MUST match /app/backend/allergen_classifier.py
const ALLERGEN_LABELS = {
  milk: 'Milk / Dairy', nuts: 'Nuts', peanuts: 'Peanuts',
  gluten: 'Gluten / Wheat', soy: 'Soy', sesame: 'Sesame',
  egg: 'Egg', fish: 'Fish', shellfish: 'Shellfish', mustard: 'Mustard',
};

// Map the free-form labels Preferences.js stores into canonical keys.
const PREF_TO_CANONICAL = {
  'peanuts': 'peanuts',
  'tree nuts': 'nuts',
  'nuts': 'nuts',
  'dairy': 'milk',
  'milk': 'milk',
  'eggs': 'egg',
  'egg': 'egg',
  'soy': 'soy',
  'wheat': 'gluten',
  'gluten': 'gluten',
  'shellfish': 'shellfish',
  'fish': 'fish',
  'sesame': 'sesame',
  'mustard': 'mustard',
};

// Razorpay checkout helper. Loads the script once, opens the popup, returns
// a promise that resolves with the payment response or rejects on user cancel.
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const openRazorpayCheckout = ({ keyId, razorpayOrderId, amount, currency, name, email, contact, description }) =>
  new Promise((resolve, reject) => {
    const options = {
      key: keyId,
      amount,
      currency,
      name: 'Cravitoo',
      description,
      order_id: razorpayOrderId,
      prefill: { name: name || '', email: email || '', contact: contact || '' },
      theme: { color: '#FF5A1F' },
      handler: (response) => resolve(response),
      modal: { ondismiss: () => reject(new Error('Payment cancelled')) },
    };
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => reject(new Error(resp.error?.description || 'Payment failed')));
    rzp.open();
  });

// Recovery path: if the /verify call is lost (network drop, app backgrounded)
// after a successful payment, poll /status so a paid customer still lands on
// their order — the webhook materialises it idempotently server-side.
const pollPaymentStatus = async (API, razorpayOrderId, { tries = 8, intervalMs = 1500 } = {}) => {
  let last = null;
  for (let i = 0; i < tries; i++) {
    try {
      const { data } = await axios.get(`${API}/payments/razorpay/status`, {
        params: { razorpay_order_id: razorpayOrderId },
        withCredentials: true,
      });
      last = data;
      if (data.order_exists) return { recovered: true, ...data };
    } catch (_) { /* keep polling */ }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return { recovered: !!(last && last.order_exists), ...(last || {}) };
};

const CartInner = ({
  compact = false,
  cartByVendor,
  totalItemsInCart,
  grandTotal,
  updateQuantity,
  onCheckout,
  submitting,
}) => {
  // When rendered inside the mobile bottom-sheet (`compact`), suffix all
  // inner data-testids with `-sheet` so they don't clash with the hidden
  // desktop sticky column that stays mounted in the DOM.
  const tid = (base) => (compact ? `${base}-sheet` : base);
  return (
    <div data-testid={compact ? 'cart-sheet-inner' : 'cart-section'} className="bg-card border border-border-light rounded-xl p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div className="flex items-center space-x-2">
          <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
          <h3 className="font-heading text-lg sm:text-xl font-medium text-text-primary">Your Cart</h3>
        </div>
        {totalItemsInCart > 0 && (
          <span data-testid={tid('cart-count')} className="bg-primary text-white text-xs font-medium px-2 py-1 rounded-full">
            {totalItemsInCart}
          </span>
        )}
      </div>
      {Object.keys(cartByVendor).length === 0 ? (
        <p data-testid={tid('empty-cart-message')} className="text-text-secondary text-center py-8 text-sm">Your cart is empty</p>
      ) : (
        <>
          {Object.entries(cartByVendor).map(([vId, vCart]) => (
            <div key={vId} className="mb-4 pb-4 border-b border-border-light last:border-b-0">
              <div className="flex items-center space-x-2 mb-3">
                <Store className="h-4 w-4 text-primary" />
                <p data-testid={tid(`cart-vendor-${vId}`)} className="font-medium text-text-primary text-sm">{vCart.vendorName}</p>
              </div>
              {vCart.items.map((item) => (
                <div key={item.id} data-testid={tid(`cart-item-${item.id}`)} className="flex justify-between items-start mb-3">
                  <div className="flex-1 pr-3">
                    <p className="text-text-primary text-sm mb-1 leading-tight">{item.name}</p>
                    <p className="text-text-secondary text-xs">₹{item.price.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => updateQuantity(vId, item.id, -1)}
                      data-testid={tid(`decrease-qty-${item.id}`)}
                      className="bg-background hover:bg-gray-200 rounded-full p-1.5 transition-colors touch-manipulation"
                    >
                      <Minus className="h-4 w-4 text-text-primary" />
                    </button>
                    <span data-testid={tid(`cart-qty-${item.id}`)} className="font-medium text-text-primary text-sm w-5 text-center">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(vId, item.id, 1)}
                      data-testid={tid(`increase-qty-${item.id}`)}
                      className="bg-background hover:bg-gray-200 rounded-full p-1.5 transition-colors touch-manipulation"
                    >
                      <Plus className="h-4 w-4 text-text-primary" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div className="border-t-2 border-border-light pt-4 mb-4">
            <div className="flex justify-between items-center">
              <span className="font-heading text-base sm:text-lg font-medium text-text-primary">Total</span>
              <span data-testid={tid('cart-total')} className="font-heading text-xl sm:text-2xl font-semibold text-primary">
                ₹{grandTotal.toFixed(2)}
              </span>
            </div>
            {Object.keys(cartByVendor).length > 1 && (
              <p className="text-xs text-text-muted mt-2">
                {Object.keys(cartByVendor).length} separate orders will be created
              </p>
            )}
          </div>
          <button
            onClick={onCheckout}
            disabled={submitting}
            data-testid={compact ? 'place-order-btn-sheet' : 'place-order-btn'}
            className="w-full bg-primary hover:bg-primary-hover text-white py-3.5 rounded-xl font-semibold transition-colors disabled:opacity-50 touch-manipulation"
          >
            {submitting ? 'Processing...' : `Proceed to Checkout · ₹${grandTotal.toFixed(0)}`}
          </button>
        </>
      )}
    </div>
  );
};

const EmployeeMenu = () => {
  const [searchParams] = useSearchParams();
  const vendorId = searchParams.get('vendor');
  const [vendors, setVendors] = useState(() => {
    try { const s = localStorage.getItem('cravitoo_vendors'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [selectedVendor, setSelectedVendor] = useState(() => {
    if (vendorId) return vendorId;
    try { return localStorage.getItem('cravitoo_last_vendor') || ''; } catch { return ''; }
  });
  const [menuItems, setMenuItems] = useState(() => {
    try {
      const lv = vendorId || localStorage.getItem('cravitoo_last_vendor') || '';
      const s = lv ? localStorage.getItem(`cravitoo_menu_${lv}`) : null;
      return s ? JSON.parse(s) : [];
    } catch { return []; }
  });
  const [menuRatings, setMenuRatings] = useState({});
  const [closedCounters, setClosedCounters] = useState(new Set());
  const [cartByVendor, setCartByVendor] = useState(() => {
    try {
      const saved = localStorage.getItem('cravitoo_cart');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [loading, setLoading] = useState(() => {
    try { const s = localStorage.getItem('cravitoo_vendors'); return !(s && JSON.parse(s).length); } catch { return true; }
  });
  const [menuLoading, setMenuLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // { paid, recovered, orders:[{vendorName,code,token,amount,finalizing}] }
  // Mobile bottom-sheet cart drawer.
  const [cartSheetOpen, setCartSheetOpen] = useState(false);
  const cartSectionRef = useRef(null);

  // Employee's saved allergies (from Preferences page), normalised to canonical keys.
  const [userAllergens, setUserAllergens] = useState([]);
  const [hideAllergenItems, setHideAllergenItems] = useState(true);
  // Menu search / veg filter / sort (default: A–Z).
  const [search, setSearch] = useState('');
  const [vegFilter, setVegFilter] = useState('all'); // all | veg | nonveg
  const [sortBy, setSortBy] = useState('az');         // az | low | high
  // Track connectivity so we can tell the employee "you're offline" instead of
  // wrongly implying their account isn't linked to a site.
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const fetchPreferences = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/preferences`, { withCredentials: true });
      const raw = data?.allergies || [];
      const canonical = Array.from(new Set(
        raw
          .map((a) => PREF_TO_CANONICAL[String(a).trim().toLowerCase()])
          .filter(Boolean),
      ));
      setUserAllergens(canonical);
    } catch (e) {
      logger.warn('Could not load allergen preferences:', e?.message || e);
    }
  }, []);

  useEffect(() => { fetchPreferences(); }, [fetchPreferences]);

  // Listen for the browser going offline/online.
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vendors`, { withCredentials: true });
      setVendors(data);
      try { localStorage.setItem('cravitoo_vendors', JSON.stringify(data)); } catch { /* quota */ }
      setSelectedVendor((cur) => {
        if (cur && data.some((v) => v.id === cur)) return cur;
        return data.length > 0 ? data[0].id : '';
      });
    } catch (error) {
      logger.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMenu = useCallback(async () => {
    setMenuLoading(true);
    try {
      const { data } = await axios.get(`${API}/menu/${selectedVendor}`, { withCredentials: true });
      setMenuItems(data);
      try { localStorage.setItem(`cravitoo_menu_${selectedVendor}`, JSON.stringify(data)); } catch { /* quota */ }
    } catch (error) {
      logger.error('Error:', error);
    } finally {
      setMenuLoading(false);
    }
  }, [selectedVendor]);

  const fetchMenuRatings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/feedback/menu-ratings?vendor_id=${selectedVendor}`, { withCredentials: true });
      const map = {};
      (data || []).forEach((r) => { map[(r.item || '').trim().toLowerCase()] = r; });
      setMenuRatings(map);
    } catch {
      setMenuRatings({});
    }
  }, [selectedVendor]);

  const fetchCounters = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/counters?vendor_id=${selectedVendor}`, { withCredentials: true });
      setClosedCounters(new Set((data || []).filter((c) => c.status === 'closed').map((c) => c.name)));
    } catch {
      setClosedCounters(new Set());
    }
  }, [selectedVendor]);

  useEffect(() => {
    fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (selectedVendor) { fetchMenu(); fetchMenuRatings(); fetchCounters(); } }, [selectedVendor, fetchMenu, fetchMenuRatings, fetchCounters]);
  useEffect(() => { if (selectedVendor) { try { localStorage.setItem('cravitoo_last_vendor', selectedVendor); } catch { /* quota */ } } }, [selectedVendor]);
  useEffect(() => {
    localStorage.setItem('cravitoo_cart', JSON.stringify(cartByVendor));
  }, [cartByVendor]);

  // Lock body scroll while the mobile cart sheet is open.
  useEffect(() => {
    document.body.style.overflow = cartSheetOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [cartSheetOpen]);

  // Recovery on load: if a payment was interrupted (page refresh / app returned)
  // after money left but before /verify finished, confirm it via /status so the
  // customer still sees their order instead of a lost payment.
  useEffect(() => {
    let raw;
    try { raw = localStorage.getItem('cravitoo_pending_rzp'); } catch (_) { raw = null; }
    if (!raw) return;
    let pend;
    try { pend = JSON.parse(raw); } catch { try { localStorage.removeItem('cravitoo_pending_rzp'); } catch (_) {} return; }
    if (!pend?.id) { try { localStorage.removeItem('cravitoo_pending_rzp'); } catch (_) {} return; }
    (async () => {
      const status = await pollPaymentStatus(API, pend.id, { tries: 4, intervalMs: 1500 });
      try { localStorage.removeItem('cravitoo_pending_rzp'); } catch (_) {}
      if (status.recovered) {
        setConfirmation({
          paid: true, recovered: true,
          orders: [{ vendorName: pend.vendorName || 'Your order', code: status.collection_code, token: status.token_number, amount: status.total_amount }],
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Instant vendor switch: paint the cached menu for that vendor immediately,
  // then the effect refetches to revalidate (stale-while-revalidate).
  const selectVendor = (vid) => {
    setSelectedVendor(vid);
    try { const s = localStorage.getItem(`cravitoo_menu_${vid}`); setMenuItems(s ? JSON.parse(s) : []); }
    catch { setMenuItems([]); }
  };

  const getCurrentVendor = () => vendors.find(v => v.id === selectedVendor);

  const isItemClosed = (item) => !!(item.counter && closedCounters.has(item.counter));

  const addToCart = (item) => {
    const vendor = getCurrentVendor();
    if (!vendor) return;
    if (isItemClosed(item)) return;
    setCartByVendor(prev => {
      const vendorCart = prev[selectedVendor] || { vendorName: vendor.name, items: [] };
      const existing = vendorCart.items.find(i => i.id === item.id);
      const newItems = existing
        ? vendorCart.items.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i)
        : [...vendorCart.items, { ...item, quantity: 1 }];
      return { ...prev, [selectedVendor]: { ...vendorCart, items: newItems } };
    });
  };

  const updateQuantity = (vId, itemId, delta) => {
    setCartByVendor(prev => {
      const vendorCart = prev[vId];
      if (!vendorCart) return prev;
      const newItems = vendorCart.items
        .map(i => i.id === itemId ? { ...i, quantity: i.quantity + delta } : i)
        .filter(i => i.quantity > 0);
      if (newItems.length === 0) {
        const { [vId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [vId]: { ...vendorCart, items: newItems } };
    });
  };

  const placeOrdersForAllVendors = async () => {
    if (submitting) return;   // hard guard against double-click
    setSubmitting(true);
    try {
      const allVendorIds = Object.keys(cartByVendor);
      if (allVendorIds.length === 0) return;

      const successfulOrders = [];   // {code, vendorName, amount, mode}
      const paidVendors = [];
      const failures = [];

      // Fetch payment mode ONCE — decides which endpoint we hit.
      let paymentMode = 'OFFLINE';
      try {
        const { data } = await axios.get(`${API}/config/payment-mode`, { withCredentials: true });
        paymentMode = (data.mode || 'OFFLINE').toUpperCase();
      } catch (_) { paymentMode = 'OFFLINE'; }

      // Only load Razorpay if we're actually going to use it.
      if (paymentMode === 'RAZORPAY') {
        const scriptOk = await loadRazorpayScript();
        if (!scriptOk) {
          alert('Could not load the payment gateway. Please check your internet and try again.');
          return;
        }
      }

      for (const vId of allVendorIds) {
        const vendorCart = cartByVendor[vId];
        const vendorName = vendorCart.vendor?.name || 'Vendor';
        const vendorAmount = (vendorCart.items || []).reduce((s, it) => s + it.price * it.quantity, 0);
        try {
          const cartPayload = {
            vendor_id: vId,
            items: vendorCart.items.map((item) => ({
              menu_item_id: item.id,
              quantity: item.quantity,
              price: item.price,
            })),
            delivery_type: 'pickup',
          };

          if (paymentMode === 'RAZORPAY') {
            // PAYMENT-FIRST FLOW —
            //   1) Ask backend to create a Razorpay order (no Cravitoo order yet)
            //   2) Open checkout, user pays
            //   3) Send signatures to /verify — backend materialises the Cravitoo order
            //      atomically and returns the collection_code + order_id.
            const { data: intent } = await axios.post(
              `${API}/payments/razorpay/checkout-intent`,
              cartPayload,
              { withCredentials: true },
            );
            // Remember this payment in case the callback is lost mid-flow.
            try {
              localStorage.setItem('cravitoo_pending_rzp', JSON.stringify({
                id: intent.razorpay_order_id, vendorName, ts: Date.now(),
              }));
            } catch (_) { /* quota */ }
            const payResp = await openRazorpayCheckout({
              keyId: intent.key_id,
              razorpayOrderId: intent.razorpay_order_id,
              amount: intent.amount,
              currency: intent.currency,
              description: `Cravitoo — ${vendorName}`,
            });
            let verified;
            try {
              const { data } = await axios.post(
                `${API}/payments/razorpay/verify`,
                {
                  razorpay_order_id: payResp.razorpay_order_id,
                  razorpay_payment_id: payResp.razorpay_payment_id,
                  razorpay_signature: payResp.razorpay_signature,
                },
                { withCredentials: true },
              );
              verified = data;
            } catch (verifyErr) {
              // Payment went through but /verify was lost — recover via /status.
              const status = await pollPaymentStatus(API, payResp.razorpay_order_id);
              if (status.recovered) {
                verified = {
                  order_id: status.order_id,
                  collection_code: status.collection_code,
                  total_amount: status.total_amount,
                  token_number: status.token_number,
                };
              } else if (status.captured) {
                // Captured but still finalising server-side; order will appear shortly.
                verified = { order_id: null, collection_code: null, total_amount: vendorAmount, token_number: null, finalizing: true };
              } else {
                throw verifyErr;
              }
            }
            try { localStorage.removeItem('cravitoo_pending_rzp'); } catch (_) { /* noop */ }
            successfulOrders.push({
              code: verified.collection_code,
              vendorName,
              amount: verified.total_amount || vendorAmount,
              mode: 'RAZORPAY',
              orderId: verified.order_id,
              token: verified.token_number,
              finalizing: !!verified.finalizing,
            });
          } else {
            // OFFLINE — old behaviour (backend creates order in pending state)
            const { data: created } = await axios.post(`${API}/orders`, cartPayload, { withCredentials: true });
            successfulOrders.push({
              code: created.collection_code,
              vendorName,
              amount: vendorAmount,
              mode: 'OFFLINE',
              orderId: created.id,
              token: created.token_number,
            });
          }
          paidVendors.push(vId);
        } catch (payErr) {
          logger.error('Payment failed for vendor', vId, payErr);
          const label = vendorName;
          const detail = payErr?.response?.data?.detail || payErr?.message || 'unknown';
          failures.push({ vId, label, message: detail });
          if (payErr?.message === 'Payment cancelled') break;
        }
      }

      // Clear ONLY the successfully-paid vendor carts.
      setCartByVendor((prev) => {
        const next = { ...prev };
        for (const vId of paidVendors) delete next[vId];
        try {
          localStorage.setItem('cravitoo_cart', JSON.stringify(next));
        } catch (_) { /* localStorage full — ignore */ }
        return next;
      });
      setCartSheetOpen(false);

      if (failures.length === 0 && successfulOrders.length > 0) {
        setConfirmation({
          paid: successfulOrders[0].mode === 'RAZORPAY',
          orders: successfulOrders,
        });
      } else if (paidVendors.length > 0) {
        const failedLabels = failures.map((f) => f.label).join(', ');
        alert(
          `${paidVendors.length} order(s) placed successfully.\n` +
          `${failures.length} vendor cart(s) still pending (${failedLabels}). ` +
          `You can retry them from the cart.`,
        );
      } else {
        // 0 paid, some failures
        const first = failures[0];
        alert(first?.message === 'Payment cancelled'
          ? 'Payment cancelled. Your cart is still saved.'
          : `Payment failed: ${first?.message || 'please try again'}. Your cart is still saved.`);
      }
    } catch (error) {
      logger.error('Error:', error);
      alert(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  const totalItemsInCart = Object.values(cartByVendor).reduce(
    (sum, vc) => sum + vc.items.reduce((s, i) => s + i.quantity, 0), 0,
  );
  const grandTotal = Object.values(cartByVendor).reduce(
    (sum, vc) => sum + vc.items.reduce((s, i) => s + (i.price * i.quantity), 0), 0,
  );

  // Whether the current vendor's menu contains any non-veg dish — the Veg/Non-veg
  // filter is only shown when there's something to filter.
  const hasNonVeg = menuItems.some((i) => !i.is_vegetarian);

  // Search + veg filter + allergen filter + sort (default A–Z) applied together.
  const displayItems = React.useMemo(() => {
    let list = menuItems;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((i) =>
        (i.name || '').toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q),
      );
    }
    if (hasNonVeg) {
      if (vegFilter === 'veg') list = list.filter((i) => i.is_vegetarian);
      else if (vegFilter === 'nonveg') list = list.filter((i) => !i.is_vegetarian);
    }
    if (hideAllergenItems && userAllergens.length > 0) {
      list = list.filter((i) => !(i.allergens || []).some((a) => userAllergens.includes(a)));
    }
    const sorted = [...list];
    if (sortBy === 'low') sorted.sort((a, b) => a.price - b.price);
    else if (sortBy === 'high') sorted.sort((a, b) => b.price - a.price);
    else sorted.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    return sorted;
  }, [menuItems, search, vegFilter, hasNonVeg, sortBy, hideAllergenItems, userAllergens]);

  const handleCheckoutTap = () => {
    // Desktop: cart is visible in the right column — scroll to it smoothly.
    // Mobile: open the bottom-sheet drawer.
    if (window.innerWidth >= 1024 && cartSectionRef.current) {
      cartSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      setCartSheetOpen(true);
    }
  };

  const CART_PROPS = {
    cartByVendor,
    totalItemsInCart,
    grandTotal,
    updateQuantity,
    onCheckout: placeOrdersForAllVendors,
    submitting,
  };

  if (loading && vendors.length === 0) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </>
    );
  }


  return (
    <>
      <Navbar />
      {confirmation && (
        <div
          data-testid="confirmation-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        >
          <div className="w-full max-w-md bg-card rounded-2xl shadow-2xl border border-border-light overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="bg-primary/10 px-6 pt-6 pb-4 text-center">
              <div className="mx-auto mb-3 h-14 w-14 rounded-full bg-primary/15 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-primary" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-text-primary">
                {confirmation.paid ? 'Payment successful' : 'Order confirmed'}
              </h2>
              <p className="text-text-secondary text-sm mt-1">
                {confirmation.recovered
                  ? 'We recovered your order after a connection hiccup.'
                  : confirmation.paid
                    ? 'Show your token at the counter to collect.'
                    : 'Pay at the counter — show your token to the vendor.'}
              </p>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[50vh] overflow-y-auto">
              {confirmation.orders.map((o, i) => (
                <div key={i} data-testid={`confirmation-order-${i}`} className="rounded-xl border border-border-light p-4">
                  <p className="text-xs text-text-secondary mb-2">{o.vendorName}{typeof o.amount === 'number' ? ` · ₹${o.amount.toFixed(2)}` : ''}</p>
                  {o.token ? (
                    <div className="flex items-center justify-center gap-3 py-2 rounded-lg bg-primary text-white" data-testid={`confirmation-token-${i}`}>
                      <span className="text-xs font-semibold uppercase tracking-widest opacity-80">Token</span>
                      <span className="font-heading text-4xl font-bold leading-none tracking-tight">{o.token}</span>
                    </div>
                  ) : o.finalizing ? (
                    <p className="text-center text-sm text-amber-700 py-2" data-testid={`confirmation-finalizing-${i}`}>
                      Payment received — your token is being generated and will appear in My Orders shortly.
                    </p>
                  ) : null}
                  {o.code && (
                    <p className="mt-2 text-center">
                      <span className="text-[11px] text-text-secondary uppercase tracking-wide">Collection code</span><br />
                      <span className="font-mono text-base font-bold text-primary" data-testid={`confirmation-code-${i}`}>{o.code}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div className="px-6 pb-6 pt-1 flex flex-col gap-2">
              <button
                data-testid="confirmation-view-orders-btn"
                onClick={() => { window.location.href = '/employee/orders'; }}
                className="w-full py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90 transition-colors"
              >
                View My Orders
              </button>
              <button
                data-testid="confirmation-close-btn"
                onClick={() => setConfirmation(null)}
                className="w-full py-2.5 rounded-xl text-text-secondary font-medium hover:bg-background transition-colors"
              >
                Keep browsing
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="min-h-screen bg-background pb-28 lg:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="mb-6 sm:mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold text-text-primary mb-4">
              Browse Menu
            </h1>
            {vendors.length === 0 ? (
              isOffline ? (
                <div data-testid="offline-empty-state" className="bg-card border border-border-light rounded-2xl p-8 sm:p-12 text-center">
                  <WifiOff className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                  <h2 className="font-heading text-lg font-semibold text-text-primary mb-2">Network is offline</h2>
                  <p className="text-text-secondary text-sm max-w-md mx-auto">
                    We can't load your menu because your device is offline. Please connect to a stable network and try again.
                  </p>
                </div>
              ) : (
                <div data-testid="no-vendors-empty-state" className="bg-card border border-border-light rounded-2xl p-8 sm:p-12 text-center">
                  <ShieldAlert className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                  <h2 className="font-heading text-lg font-semibold text-text-primary mb-2">No menu available yet</h2>
                  <p className="text-text-secondary text-sm max-w-md mx-auto">
                    Your account isn't linked to a site with active vendors yet. Please contact your Cravitoo admin to get your office site assigned.
                  </p>
                </div>
              )
            ) : (
            <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-2 snap-x snap-mandatory">
              {vendors.map((vendor) => (
                <button
                  key={vendor.id}
                  data-testid={`vendor-tab-${vendor.id}`}
                  onClick={() => selectVendor(vendor.id)}
                  className={`flex-shrink-0 snap-start px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium text-sm sm:text-base transition-colors ${
                    selectedVendor === vendor.id
                      ? 'bg-primary text-white'
                      : 'bg-card border border-border-light text-text-secondary hover:text-text-primary hover:border-primary'
                  }`}
                >
                  {vendor.name}
                </button>
              ))}
            </div>
            )}
          </div>

          {vendors.length > 0 && (

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="lg:col-span-2">
              {userAllergens.length > 0 && (
                <div
                  data-testid="allergen-filter-banner"
                  className="mb-4 flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3"
                >
                  <ShieldAlert className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div className="flex-1 min-w-[140px]">
                    <p className="text-sm font-medium text-amber-900">
                      Your saved allergies:{' '}
                      {userAllergens.map((a) => ALLERGEN_LABELS[a]).join(', ')}
                    </p>
                    <p className="text-xs text-amber-700">
                      {hideAllergenItems
                        ? 'Items containing these are hidden.'
                        : 'Items containing these are highlighted below — check ingredients before ordering.'}
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-amber-900 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="hide-allergen-toggle"
                      checked={hideAllergenItems}
                      onChange={(e) => setHideAllergenItems(e.target.checked)}
                      className="accent-amber-500 h-4 w-4"
                    />
                    Hide items with my allergies
                  </label>
                </div>
              )}
              {/* Search + veg filter + sort controls */}
              <div className="mb-4 space-y-3" data-testid="menu-controls">
                <div className="relative">
                  <Search className="h-4 w-4 text-text-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search dishes…"
                    data-testid="menu-search-input"
                    className="w-full bg-card border border-border-light rounded-xl pl-10 pr-9 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      data-testid="menu-search-clear"
                      aria-label="Clear search"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  {hasNonVeg ? (
                    <div className="flex items-center gap-1.5" data-testid="veg-filter">
                      {[
                        { k: 'all', label: 'All' },
                        { k: 'veg', label: 'Veg' },
                        { k: 'nonveg', label: 'Non-veg' },
                      ].map((f) => (
                        <button
                          key={f.k}
                          onClick={() => setVegFilter(f.k)}
                          data-testid={`veg-filter-${f.k}`}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            vegFilter === f.k
                              ? 'bg-primary text-white border-primary'
                              : 'bg-card text-text-secondary border-border-light hover:border-primary'
                          }`}
                        >
                          {f.k !== 'all' && (
                            <span
                              className={`inline-flex items-center justify-center h-3.5 w-3.5 border ${
                                f.k === 'veg' ? 'border-green-600' : 'border-red-600'
                              }`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${f.k === 'veg' ? 'bg-green-600' : 'bg-red-600'}`} />
                            </span>
                          )}
                          {f.label}
                        </button>
                      ))}
                    </div>
                  ) : <span />}
                  <div className="flex items-center gap-2">
                    <ArrowDownAZ className="h-4 w-4 text-text-muted" />
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      data-testid="menu-sort-select"
                      className="bg-card border border-border-light rounded-lg px-2.5 py-1.5 text-xs font-medium text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
                    >
                      <option value="az">Name (A–Z)</option>
                      <option value="low">Price: Low → High</option>
                      <option value="high">Price: High → Low</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {menuLoading && menuItems.length === 0 && [0, 1, 2, 3].map((i) => (
                  <div key={`skeleton-${i}`} data-testid={`menu-skeleton-${i}`} className="bg-card border border-border-light rounded-xl overflow-hidden animate-pulse">
                    <div className="h-40 sm:h-48 bg-border-light/60" />
                    <div className="p-4 sm:p-6 space-y-3">
                      <div className="h-4 w-2/3 bg-border-light/70 rounded" />
                      <div className="h-3 w-full bg-border-light/50 rounded" />
                      <div className="flex justify-between items-center pt-2">
                        <div className="h-5 w-16 bg-border-light/70 rounded" />
                        <div className="h-8 w-20 bg-border-light/60 rounded-lg" />
                      </div>
                    </div>
                  </div>
                ))}
                {!menuLoading && displayItems.length === 0 && menuItems.length > 0 && (
                  <div data-testid="menu-no-results" className="sm:col-span-2 bg-card border border-border-light rounded-xl p-8 text-center">
                    <Search className="h-10 w-10 text-text-muted mx-auto mb-3" />
                    <p className="text-text-secondary text-sm">No dishes match your search or filters.</p>
                  </div>
                )}
                {displayItems
                  .map((item) => {
                    const allergens = item.allergens || [];
                    const conflicts = allergens.filter((a) => userAllergens.includes(a));
                    return (
                  <div
                    key={item.id}
                    data-testid={`menu-item-${item.id}`}
                    className={`bg-card border rounded-xl overflow-hidden hover:shadow-md transition-shadow ${
                      conflicts.length > 0 ? 'border-red-300 ring-1 ring-red-200' : 'border-border-light'
                    }`}
                  >
                    {item.image_url && (
                      <div className="relative">
                        <img src={item.image_url} alt={item.name} className="w-full h-40 sm:h-48 object-cover" loading="lazy" />
                        {item.image_source === 'vendor_upload' && (
                          <div
                            data-testid={`vendor-photo-credit-${item.id}`}
                            className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 pointer-events-none"
                            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                              <circle cx="12" cy="13" r="4"/>
                            </svg>
                            <span>Photo by {getCurrentVendor()?.name || 'vendor'}</span>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="p-4 sm:p-6">
                      <div className="flex justify-between items-start mb-2 gap-2">
                        <div className="min-w-0">
                          <h3 className="font-heading text-base sm:text-lg font-medium text-text-primary leading-tight">{item.name}</h3>
                          {menuRatings[(item.name || '').trim().toLowerCase()] && (
                            <div data-testid={`menu-item-rating-${item.id}`} className="flex items-center gap-1 mt-1">
                              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                              <span className="text-sm font-semibold text-amber-600">{menuRatings[(item.name || '').trim().toLowerCase()].avg}</span>
                              <span className="text-xs text-text-secondary">· {menuRatings[(item.name || '').trim().toLowerCase()].count} rating{menuRatings[(item.name || '').trim().toLowerCase()].count === 1 ? '' : 's'}</span>
                            </div>
                          )}
                        </div>
                        <VegIndicator isVeg={!!item.is_vegetarian} size="md" />
                      </div>
                      <p className="text-text-secondary text-sm mb-3 line-clamp-2">{item.description}</p>
                      {allergens.length > 0 && (
                        <div
                          data-testid={`menu-item-allergens-${item.id}`}
                          className="flex items-start gap-1.5 mb-3 flex-wrap"
                        >
                          <ShieldAlert className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${conflicts.length ? 'text-red-600' : 'text-amber-600'}`} />
                          <div className="flex flex-wrap gap-1">
                            {allergens.map((a) => {
                              const isConflict = userAllergens.includes(a);
                              return (
                                <span
                                  key={a}
                                  className={`text-[10px] px-2 py-0.5 rounded-full border ${
                                    isConflict
                                      ? 'bg-red-100 border-red-300 text-red-800 font-medium'
                                      : 'bg-amber-50 border-amber-200 text-amber-800'
                                  }`}
                                  title={ALLERGEN_LABELS[a]}
                                >
                                  {ALLERGEN_LABELS[a] || a}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <p className="text-text-primary font-semibold text-lg">₹{item.price.toFixed(2)}</p>
                        {isItemClosed(item) ? (
                          <span data-testid={`item-closed-${item.id}`} className="text-xs font-medium px-3 py-2 rounded-lg bg-red-50 text-red-700 border border-red-200">
                            {item.counter} closed
                          </span>
                        ) : (
                          <button
                            onClick={() => addToCart(item)}
                            data-testid={`add-to-cart-${item.id}`}
                            className="bg-primary hover:bg-primary-hover text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 touch-manipulation"
                          >
                            <Plus className="h-4 w-4" />
                            <span>Add</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                    );
                  })}
              </div>
            </div>

            {/* Desktop sticky cart column — hidden on mobile (mobile uses bottom sheet). */}
            <div ref={cartSectionRef} className="hidden lg:block">
              <div className="sticky top-24">
                <CartInner {...CART_PROPS} />
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Floating checkout bar — visible only on mobile/tablet when the cart has items. */}
      {totalItemsInCart > 0 && (
        <button
          onClick={handleCheckoutTap}
          data-testid="floating-checkout-btn"
          aria-label={`Open cart — ${totalItemsInCart} items, ₹${grandTotal.toFixed(0)}`}
          className="lg:hidden fixed bottom-4 left-4 right-4 z-40 flex items-center justify-between bg-primary hover:bg-primary-hover text-white px-5 py-4 rounded-2xl shadow-xl active:scale-[0.98] transition-transform touch-manipulation"
        >
          <span className="flex items-center gap-3">
            <span className="relative">
              <ShoppingCart className="h-5 w-5" />
              <span className="absolute -top-2 -right-2 bg-white text-primary text-xs font-bold rounded-full h-5 min-w-[1.25rem] flex items-center justify-center px-1">
                {totalItemsInCart}
              </span>
            </span>
            <span className="font-semibold text-sm">
              {totalItemsInCart} item{totalItemsInCart === 1 ? '' : 's'} · ₹{grandTotal.toFixed(0)}
            </span>
          </span>
          <span className="font-semibold text-sm">View cart →</span>
        </button>
      )}

      {/* Mobile bottom-sheet cart drawer */}
      {cartSheetOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="cart-sheet-overlay">
          <button
            aria-label="Close cart"
            onClick={() => setCartSheetOpen(false)}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[90vh] overflow-y-auto bg-background rounded-t-3xl shadow-2xl animate-[slideUp_.25s_ease-out]">
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur flex items-center justify-between px-4 py-3 border-b border-border-light">
              <div className="flex-1 flex justify-center">
                <div className="w-10 h-1.5 bg-gray-300 rounded-full" />
              </div>
              <button
                onClick={() => setCartSheetOpen(false)}
                data-testid="close-cart-sheet"
                aria-label="Close cart"
                className="absolute right-3 top-3 p-2 rounded-full hover:bg-background-alt"
              >
                <X className="h-5 w-5 text-text-secondary" />
              </button>
            </div>
            <div className="px-4 pb-6 pt-2">
              <CartInner compact {...CART_PROPS} />
              {Object.keys(cartByVendor).length > 0 && (
                <button
                  onClick={() => setCartSheetOpen(false)}
                  className="w-full mt-3 text-text-secondary text-sm py-2 flex items-center justify-center gap-1"
                  data-testid="keep-shopping-btn"
                >
                  <ChevronDown className="h-4 w-4" /> Keep shopping
                </button>
              )}
            </div>
          </div>
          <style>{`
            @keyframes slideUp {
              from { transform: translateY(100%); }
              to { transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
    </>
  );
};

export default EmployeeMenu;
